import { useState, useEffect, useCallback, lazy, Suspense, forwardRef, useImperativeHandle, useRef } from 'react'
import { Eye, Pencil, Loader2 } from 'lucide-react'
import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'
import { useCommandRegistry, type Command } from '@papan/core-ui/ui'
import { stripPointerSuffix } from '@papan/types'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ModeToggle } from './ModeToggle.js'
import { ReadOnlyBadge } from './ReadOnlyBadge.js'

const LazyDataGridViewer = lazy(() =>
  import('./DataGridViewer.js').then(m => ({ default: m.DataGridViewer }))
)
const LazyUniverModifier = lazy(() =>
  import('./UniverModifier.js').then(m => ({ default: m.UniverModifier }))
)

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

interface SpreadsheetEditorProps {
  room: string
  fileId: string
  fileName?: string
  parentId?: string | null
  isActive?: boolean
  isWorkbookGroup?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** (#942) The viewer may not write: View only — no Edit mode (the Univer
   *  spreadsheet, add-sheet, paste), the padlock instead. The grid stays on the
   *  live room, so others' edits still show. The collab server already drops a
   *  read-only client's writes; this keeps the screen from offering them. */
  readOnly?: boolean
  /** Shared view mode: 'preview' = View (AG Grid), 'source' = Edit (Univer) */
  initialViewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
}

type Mode = 'view' | 'edit'

export const SpreadsheetEditor = forwardRef<EditorEditHandle, SpreadsheetEditorProps>(
  function SpreadsheetEditor({ room, fileId, fileName, parentId, isActive, isWorkbookGroup, readOnly = false, onDirtyChange, onSaveRef, onDiscardRef, registerUndoRedo, initialViewMode, onViewModeChange }, ref) {
    // Map shared mode ('preview'/'source') to local mode ('view'/'edit')
    const toLocal = (m?: 'preview' | 'source'): Mode => m === 'source' ? 'edit' : 'view'
    const toShared = (m: Mode): 'preview' | 'source' => m === 'edit' ? 'source' : 'preview'

    const [chosenMode, _setMode] = useState<Mode>(toLocal(initialViewMode))
    // Read-only is always View — even when the tab remembers Edit.
    const mode: Mode = readOnly ? 'view' : chosenMode
    const setMode = useCallback((m: Mode) => {
      _setMode(m)
      onViewModeChange?.(toShared(m))
    }, [onViewModeChange])
    // Sync external mode changes (e.g., previewer changed mode)
    useEffect(() => {
      if (initialViewMode != null) _setMode(toLocal(initialViewMode))
    }, [initialViewMode])

    const [connected, setConnected] = useState(false)
    // Lifted from the child viewer/modifier (which own the Yjs connection) so the
    // wrapper toolbar can render the shared status badge — including the
    // "Reconnecting…" state during a reconnect rebuild.
    const [reloading, setReloading] = useState(false)
    const [gaveUp, setGaveUp] = useState(false)
    // `gaveUp` is lifted from whichever child owns the Yjs socket (both the
    // edit-mode UniverModifier and the view-mode grid report it). On a mode
    // switch the outgoing child doesn't emit `false` on unmount, so clear it
    // here to avoid a stale "Disconnected" frame until the incoming child's
    // effect re-reports its own give-up state.
    useEffect(() => { setGaveUp(false) }, [mode])
    const editorRef = useRef<EditorEditHandle>(null)
    // DataGridViewer (view mode) writes row selection here
    const viewSelectionRef = useRef<{ text: string; rowCount: number } | null>(null)

    useImperativeHandle(ref, () => ({
      copy: () => editorRef.current?.copy(),
      cut: () => editorRef.current?.cut(),
      paste: () => editorRef.current?.paste(),
      delete: () => editorRef.current?.delete(),
      getSelection: () => {
        // Edit mode: delegate to Univer
        if (editorRef.current?.getSelection) return editorRef.current.getSelection()
        // View mode: read from DataGridViewer row selection
        const vs = viewSelectionRef.current
        if (!vs) return null
        return {
          text: vs.text, fileName: fileName || '', filePath: '', fileId,
          startLine: 0, endLine: 0,
          kind: 'cells' as const,
          cellRange: vs.rowCount === 1 ? '1 row' : `${vs.rowCount} rows`,
        }
      },
      focusEditor: () => editorRef.current?.focusEditor?.(),
    }), [fileName, fileId])

    // Register spreadsheet commands — enabled in edit mode (Univer handles natively)
    const cmdRegistry = useCommandRegistry()
    const modeRef = useRef(mode)
    modeRef.current = mode
    const editorRefForCmd = useRef(editorRef)
    editorRefForCmd.current = editorRef

    useEffect(() => {
      return cmdRegistry.registerScope({
        scopeId: `editor:sheet:${fileId}`,
        parentScopeId: 'editor',
        get commands(): Command[] {
          const isEdit = modeRef.current === 'edit'
          return [
            { id: 'edit.copy', label: 'Copy', shortcut: { key: 'c', mod: true }, execute: () => editorRefForCmd.current.current?.copy(), enabled: isEdit, menuGroup: 'edit', menuOrder: 10 },
            { id: 'edit.cut', label: 'Cut', shortcut: { key: 'x', mod: true }, execute: () => editorRefForCmd.current.current?.cut(), enabled: isEdit, menuGroup: 'edit', menuOrder: 11 },
            { id: 'edit.paste', label: 'Paste', shortcut: { key: 'v', mod: true }, execute: () => editorRefForCmd.current.current?.paste(), enabled: isEdit, menuGroup: 'edit', menuOrder: 12, separator: 'after' },
            { id: 'edit.delete', label: 'Delete', shortcut: { key: 'Backspace' }, execute: () => editorRefForCmd.current.current?.delete(), enabled: isEdit, menuGroup: 'edit', menuOrder: 50, danger: true },
          ]
        },
      })
    }, [fileId, cmdRegistry])

    // Set active scope when tab becomes active (requestAnimationFrame to fire after TabContent)
    // Only in edit mode — in view mode, DataGridViewer sets its own scope
    useEffect(() => {
      if (isActive && mode === 'edit') {
        const raf = requestAnimationFrame(() => cmdRegistry.setActiveScope(`editor:sheet:${fileId}`))
        return () => cancelAnimationFrame(raf)
      }
    }, [isActive, mode, fileId, cmdRegistry])

    // Also set on mousedown for returning from explorer (edit mode only)
    const [sheetEl, setSheetEl] = useState<HTMLDivElement | null>(null)
    const sheetCallbackRef = useCallback((el: HTMLDivElement | null) => setSheetEl(el), [])
    useEffect(() => {
      if (!sheetEl) return
      const handler = () => {
        if (modeRef.current === 'edit') cmdRegistry.setActiveScope(`editor:sheet:${fileId}`)
      }
      sheetEl.addEventListener('mousedown', handler, true)
      return () => sheetEl.removeEventListener('mousedown', handler, true)
    }, [sheetEl, fileId, cmdRegistry])

    return (
      <div ref={sheetCallbackRef} className="flex-1 flex flex-col overflow-hidden">
        {/* Mode Toggle Bar */}
        <EditorToolbar
          mode={readOnly ? undefined : (
            <ModeToggle
              options={[
                { value: 'view', label: 'View', icon: <Eye size={14} /> },
                { value: 'edit', label: 'Edit', icon: <Pencil size={14} /> },
              ]}
              value={mode}
              onChange={setMode}
            />
          )}
          status={<><EditorShareMenu fileId={fileId} fileName={fileName} /><CollabStatusBadge connected={connected} reloading={reloading} gaveUp={gaveUp} />{readOnly && <ReadOnlyBadge />}</>}
        />

        {/* Content */}
        {mode === 'view' ? (
          <Suspense fallback={LoadingSpinner}>
            <LazyDataGridViewer fileId={fileId} fileName={fileName} room={room}
              onDirtyChange={readOnly ? undefined : onDirtyChange} onSaveRef={readOnly ? undefined : onSaveRef} onDiscardRef={readOnly ? undefined : onDiscardRef} onConnectedChange={setConnected} onReloadingChange={setReloading} onGaveUpChange={setGaveUp} selectionRef={viewSelectionRef} isActive={isActive} />
          </Suspense>
        ) : (
          <Suspense fallback={LoadingSpinner}>
            <LazyUniverModifier
              ref={editorRef}
              room={room}
              fileId={fileId}
              fileName={fileName}
              isWorkbookGroup={isWorkbookGroup}
              onWbAddSheetRequest={(sheetCount, sheetNames) => {
                // Inner name for pointer rows: `data.csv.shared` derives
                // sibling sheets as `data_sheet_N.csv`, not `…shared_sheet_N.shared`.
                const innerName = stripPointerSuffix(fileName || 'sheet.csv')
                const ext = innerName.split('.').pop()?.toLowerCase() || 'csv'
                const base = innerName.replace(/\.(csv|tsv)$/i, '')
                const nameSet = new Set(sheetNames.map(n => n.toLowerCase()))
                let n = sheetCount + 1
                while (nameSet.has(`${base}_sheet_${n}`.toLowerCase())) n++
                const defaultName = `${base}_sheet_${n}.${ext}`
                window.dispatchEvent(new CustomEvent('papan:wb-open-add-sheet-dialog', {
                  detail: { defaultName, parentId, sourceFileId: fileId },
                }))
              }}
              onDirtyChange={onDirtyChange}
              onSaveRef={onSaveRef}
              onDiscardRef={onDiscardRef}
              registerUndoRedo={registerUndoRedo}
              onConnectedChange={setConnected}
              onReloadingChange={setReloading}
              onGaveUpChange={setGaveUp}
            />
          </Suspense>
        )}
      </div>
    )
  }
)
